import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { buscarDashboardDetalhado } from 'features/financeiro/services/financeiroService';
import { getCompanyContext } from 'utils/setCompanyContext';
import { useDeduplicatedRequest } from 'hooks/useDeduplicatedRequest';
import { usePrefetch } from 'hooks/usePrefetch';

// Cache in-memory com TTL para stale-while-revalidate
const cache = new Map();
const STALE_TIME = 5 * 60 * 1000; // 5 minutos para considerar dados stale
const CACHE_TIME = 10 * 60 * 1000; // 10 minutos para expirar cache completamente

/**
 * Gera chave de cache baseada nos parâmetros da requisição
 */
const generateCacheKey = (empresa, dataInicio, dataFim) => {
  return `empresas-resumo-${empresa}-${dataInicio}-${dataFim}`;
};

/**
 * Verifica se os dados estão stale (precisam de refresh em background)
 */
const isStale = (cacheEntry) => {
  if (!cacheEntry) return true;
  const now = Date.now();
  return (now - cacheEntry.timestamp) > STALE_TIME;
};

/**
 * Verifica se os dados estão expirados (precisam de nova requisição)
 */
const isExpired = (cacheEntry) => {
  if (!cacheEntry) return true;
  const now = Date.now();
  return (now - cacheEntry.timestamp) > CACHE_TIME;
};

/**
 * Limpa entradas expiradas do cache
 */
const cleanupExpiredEntries = () => {
  const now = Date.now();
  for (const [key, entry] of cache.entries()) {
    if ((now - entry.timestamp) > CACHE_TIME) {
      cache.delete(key);
    }
  }
};

/**
 * Processa os dados de uma empresa a partir da resposta da API
 */
const processEmpresaData = async (empresa, response) => {
  // Verificar se response não é null/undefined
  if (!response) {
    return {
      ...empresa,
      receitas: 0,
      despesas: 0,
      resultado: 0,
      percentual: 0,
      tendencia: '0.0',
      tendenciaPositiva: true,
      status: 'ERRO',
      success: false,
      error: 'Resposta da API é null'
    };
  }
  
  if (response.success && response.data) {
    // Normalizar estrutura de resposta
    let data;
    if (Array.isArray(response.data)) {
      data = response.data[0] || response.data;
    } else {
      data = response.data;
    }

    // Extrair KPIs
    let kpis = data.kpis || {};
    if (!kpis || Object.keys(kpis).length === 0) {
      if (data.entradas || data.saidas || data.caixa_liquido) {
        kpis = {
          entradas: data.entradas,
          saidas: data.saidas,
          caixa_liquido: data.caixa_liquido,
          entradas_variacao_pct: data.entradas_variacao_pct
        };
      } else if (data.data && data.data.kpis) {
        kpis = data.data.kpis;
      } else if (data.data && (data.data.entradas || data.data.saidas)) {
        kpis = {
          entradas: data.data.entradas,
          saidas: data.data.saidas,
          caixa_liquido: data.data.caixa_liquido,
          entradas_variacao_pct: data.data.entradas_variacao_pct
        };
      }
    }

    // Cálculos específicos para o componente de resumo
    const receitas = parseFloat(kpis.entradas_liquidas || kpis.entradas || 0);
    const receitasBrutas = parseFloat(kpis.entradas_brutas || kpis.entradas || 0);
    const despesas = parseFloat(kpis.saidas || 0);
    let resultado = parseFloat(kpis.caixa_liquido || 0);

    if (receitas === 0 && despesas === 0 && resultado === 0) {
      if (data.fluxoCaixa && Array.isArray(data.fluxoCaixa) && data.fluxoCaixa.length > 0) {
        const fluxoAtual = data.fluxoCaixa[0];
        const r = parseFloat(fluxoAtual.entradas || 0);
        const d = parseFloat(fluxoAtual.saidas || 0);
        resultado = r - d;
      }
    }

    const percentual = receitas > 0 ? (resultado / receitas) * 100 : 0;
    const tendenciaEntradas = parseFloat(kpis.entradas_variacao_pct || 0);
    const tendencia = tendenciaEntradas >= 0 ? `+${tendenciaEntradas.toFixed(1)}` : `${tendenciaEntradas.toFixed(1)}`;

    return {
      ...empresa,
      receitas,
      receitasBrutas,
      despesas,
      resultado,
      percentual: Math.max(0, Math.min(100, percentual)),
      tendencia,
      tendenciaPositiva: tendenciaEntradas >= 0,
      status: 'DADOS CARREGADOS',
      success: true
    };
  } else {
    return {
      ...empresa,
      receitas: 0,
      despesas: 0,
      resultado: 0,
      percentual: 0,
      tendencia: '0.0',
      tendenciaPositiva: true,
      status: 'SEM DADOS',
      success: false,
      error: response.error || 'Erro ao carregar dados'
    };
  }
};

/**
 * Hook específico para o componente EmpresasResumoCards
 * Otimizado para o dashboard principal com foco em performance e UX
 */
export const useEmpresasResumoCards = () => {
  const [dadosEmpresas, setDadosEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [empresasCarregadas, setEmpresasCarregadas] = useState(new Set());
  const requestIdRef = useRef(0);
  const isVisibleRef = useRef(true);
  const hasLoadedRef = useRef(false); // Flag para evitar carregamentos duplicados
  const dadosEmpresasRef = useRef(dadosEmpresas);
  const loadingRef = useRef(loading);
  const empresasCarregadasRef = useRef(empresasCarregadas);
  
  // Hook para deduplicação de requisições
  const { executeRequest, hasPendingRequest } = useDeduplicatedRequest();
  
  // Hook para prefetch
  const { getPrefetchedData } = usePrefetch();

  // Lista das empresas do grupo (específica para o dashboard principal)
  const empresas = useMemo(() => [
    { id: 1, nome: 'MOV Fibra', icone: 'Wifi', cor: 'purple' },
    { id: 2, nome: 'XT Fibra', icone: 'Box', cor: 'purple' },
    { id: 3, nome: 'MOV Fit', icone: 'Dumbbell', cor: 'purple' }
  ], []);

  const getEmpresasAlvo = useCallback(() => {
    const ctx = getCompanyContext();    
    if (ctx?.isGroup || ctx?.isConsolidated) {
      return empresas;
    }
    
    const currentName = ctx?.companies?.[0]?.name;    
    if (!currentName) {
      return empresas; // fallback
    }
    
    const found = empresas.find(e => e.nome === currentName);
    
    return found ? [found] : [{ id: 0, nome: currentName, icone: 'Wifi', cor: 'purple' }];
  }, [empresas]);

  useEffect(() => {
    dadosEmpresasRef.current = dadosEmpresas;
  }, [dadosEmpresas]);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    empresasCarregadasRef.current = empresasCarregadas;
  }, [empresasCarregadas]);

  const carregarDadosEmpresas = useCallback(async (forceRefresh = false) => {
    // Evitar execuções duplicadas
    if (!forceRefresh && hasLoadedRef.current) {
      return;
    }
    
    // Verificar se já temos dados válidos e não é um refresh forçado
    if (!forceRefresh && dadosEmpresasRef.current.length > 0) {
      const alvo = getEmpresasAlvo();
      const todasTemDadosValidos = alvo.every(empresa => {
        const dadosEmpresa = dadosEmpresasRef.current.find(d => d.id === empresa.id);
        return dadosEmpresa && 
               dadosEmpresa.status === 'DADOS CARREGADOS' && 
               dadosEmpresa.success === true;
      });
      
      // Se todas as empresas já têm dados válidos, não recarregar
      if (todasTemDadosValidos) {
        return;
      }
    }
    
    
    // Marcar como executando para evitar duplicatas
    hasLoadedRef.current = true;
    
    // Limpar cache expirado
    cleanupExpiredEntries();
    
    // Limpar timeout anterior se existir
    if (window.empresasResumoTimeout) {
      clearTimeout(window.empresasResumoTimeout);
      window.empresasResumoTimeout = null;
    }
    
    setLoading(true);
    setError(null);
    setEmpresasCarregadas(new Set());
    const thisRequest = ++requestIdRef.current;

    try {
      const alvo = getEmpresasAlvo();      
      // Inicializar com dados antigos ou estrutura base para percepção de velocidade
      if (dadosEmpresasRef.current.length > 0 && !forceRefresh) {
        // Manter dados antigos durante recarregamento
        setDadosEmpresas(prev => prev.map(empresa => ({
          ...empresa,
          status: 'ATUALIZANDO...',
          success: true // Manter como sucesso para não mostrar erro
        })));
      } else {
        // Primeira carga - mostrar estrutura base
        setDadosEmpresas(alvo.map(empresa => ({
          ...empresa,
          receitas: 0,
          receitasBrutas: 0,
          despesas: 0,
          resultado: 0,
          percentual: 0,
          tendencia: '0.0',
          tendenciaPositiva: true,
          status: 'CARREGANDO...',
          success: false
        })));
      }

      // Processar empresas em paralelo para melhor performance
      const dataInicio = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0];
      const dataFim = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().split('T')[0];
      
      // Função para processar uma empresa individual
      const processarEmpresa = async (empresa) => {
        try {
          const cacheKey = generateCacheKey(empresa.nome, dataInicio, dataFim);
          const cacheEntry = cache.get(cacheKey);
          
          // Função para atualizar dados de uma empresa específica
          const updateEmpresaData = (newData) => {
            if (thisRequest === requestIdRef.current) {              
              setDadosEmpresas(prev => 
                prev.map(item => 
                  item.id === empresa.id ? newData : item
                )
              );
              
              // Marcar empresa como carregada
              setEmpresasCarregadas(prev => {
                const newSet = new Set(prev);
                newSet.add(empresa.id);
                return newSet;
              });
            }
          };
          
          // Se temos dados em cache e não é refresh forçado
          if (cacheEntry && !forceRefresh && !isExpired(cacheEntry)) {
            // Se os dados estão stale, fazer refresh em background APENAS se não há dados válidos
            if (isStale(cacheEntry)) {
              // Atualizar imediatamente com dados cached
              updateEmpresaData(cacheEntry.data);
              
              // Disparar refresh em background APENAS se os dados cached estão vazios ou com erro
              const dadosCached = cacheEntry.data;
              const dadosInvalidos = !dadosCached || 
                                   dadosCached.status === 'ERRO' || 
                                   dadosCached.status === 'SEM DADOS' ||
                                   (dadosCached.receitas === 0 && dadosCached.despesas === 0 && dadosCached.resultado === 0);
              
              if (dadosInvalidos) {
                setTimeout(async () => {
                  try {
                    const params = {
                      empresa: empresa.nome,
                      data_inicio: dataInicio,
                      data_fim: dataFim
                    };
                    
                    const requestKey = `empresas-resumo-${empresa.nome}-${dataInicio}-${dataFim}`;
                    const response = await executeRequest(
                      buscarDashboardDetalhado,
                      requestKey,
                      params,
                      'POST',
                      60000
                    );
                    
                    if (response && response.success && response.data) {
                      const processedData = await processEmpresaData(empresa, response);
                      // Atualizar cache com novos dados
                      cache.set(cacheKey, {
                        data: processedData,
                        timestamp: Date.now()
                      });
                      
                      // Atualizar estado apenas se ainda é a requisição atual
                      updateEmpresaData(processedData);
                    }
                  } catch (error) {
                    console.warn('[EmpresasResumoCards] Background refresh failed:', error);
                  }
                }, 0);
              }
              
              return { empresa, data: cacheEntry.data, fromCache: true }; // Dados já atualizados
            } else {
              // Dados ainda frescos, atualizar imediatamente
              updateEmpresaData(cacheEntry.data);
              return { empresa, data: cacheEntry.data, fromCache: true };
            }
          }

          // Dados não estão em cache ou estão expirados, fazer nova requisição
          const params = {
            empresa: empresa.nome,
            data_inicio: dataInicio,
            data_fim: dataFim
          };

          // Verificar se já existe requisição em voo para esta empresa
          const requestKey = `empresas-resumo-${empresa.nome}-${dataInicio}-${dataFim}`;
          if (hasPendingRequest(requestKey)) {
            return;
          }

          // Verificar se existe dados de prefetch
          const prefetchKey = `buscarDashboardDetalhado-${empresa.nome}`;
          const prefetchedData = getPrefetchedData(prefetchKey);
          
          let response;
          if (prefetchedData) {
            response = prefetchedData;
          } else {
            
            // Tentar requisição com retry automático
            let retryCount = 0;
            const maxRetries = 2;
            
            while (retryCount <= maxRetries) {
              try {
                response = await executeRequest(
                  buscarDashboardDetalhado,
                  requestKey,
                  params,
                  'POST',
                  30000
                );
                break; // Sucesso, sair do loop
              } catch (error) {
                retryCount++;
                console.warn(`[EmpresasResumoCards] Erro na requisição para ${empresa.nome} (tentativa ${retryCount}/${maxRetries + 1}):`, error);
                
                if (retryCount <= maxRetries) {
                  // Aguardar antes de tentar novamente
                  const delayMs = 1000 * retryCount;
                  await new Promise(resolve => setTimeout(resolve, delayMs));
                } else {
                  // Última tentativa falhou, lançar erro
                  throw error;
                }
              }
            }
          }
          
          // Verificar se response é válido antes de processar
          if (!response) {
            console.warn(`[EmpresasResumoCards] Response é null para ${empresa.nome}`);
            const errorData = {
              ...empresa,
              receitas: 0,
              despesas: 0,
              resultado: 0,
              percentual: 0,
              tendencia: '0.0',
              tendenciaPositiva: true,
              status: 'ERRO',
              success: false,
              error: 'Resposta da API é null'
            };
            updateEmpresaData(errorData);
            return { empresa, data: errorData, error: 'Response is null' };
          }
          
          // Processar dados da empresa
          const processedData = await processEmpresaData(empresa, response);
          
          // Salvar no cache
          cache.set(cacheKey, {
            data: processedData,
            timestamp: Date.now()
          });
          
          // Atualizar dados da empresa imediatamente
          updateEmpresaData(processedData);
          
          return { empresa, data: processedData, fromCache: false };
          
        } catch (error) {
          console.error(`[EmpresasResumoCards] Erro ao processar empresa ${empresa.nome}:`, error);
          
          const errorData = {
            ...empresa,
            receitas: 0,
            despesas: 0,
            resultado: 0,
            percentual: 0,
            tendencia: '0.0',
            tendenciaPositiva: true,
            status: 'ERRO',
            success: false,
            error: error.message || 'Erro desconhecido'
          };
          
          // Atualizar com dados de erro imediatamente
          if (thisRequest === requestIdRef.current) {
            setDadosEmpresas(prev => 
              prev.map(item => 
                item.id === empresa.id ? errorData : item
              )
            );
            
            // Marcar empresa como carregada mesmo com erro
            setEmpresasCarregadas(prev => {
              const newSet = new Set(prev);
              newSet.add(empresa.id);
              return newSet;
            });
          }
          
          return { empresa, data: errorData, error: error.message };
        }
      };

      // Executar todas as requisições em paralelo
      const resultados = await Promise.allSettled(
        alvo.map(empresa => processarEmpresa(empresa))
      );
      
      // Processar resultados
      resultados.forEach((resultado, index) => {
        if (resultado.status === 'fulfilled') {
          const { empresa, error } = resultado.value;
          if (error) {
            console.warn(`[EmpresasResumoCards] Aviso para ${empresa.nome}:`, error);
          }
        } else {
          console.error(`[EmpresasResumoCards] Erro crítico para empresa ${alvo[index].nome}:`, resultado.reason);
        }
      });
      
    } catch (error) {
      setError('Erro ao carregar dados das empresas');
      const alvo = getEmpresasAlvo();
      setDadosEmpresas(alvo.map(empresa => ({
        ...empresa,
        receitas: 0,
        despesas: 0,
        resultado: 0,
        percentual: 0,
        tendencia: '0.0',
        tendenciaPositiva: true,
        status: 'ERRO',
        success: false
      })));
    }
    
    // Finalizar loading quando todas as empresas forem carregadas
    const checkLoadingComplete = () => {
      if (thisRequest === requestIdRef.current) {
        const alvo = getEmpresasAlvo();
        const todasCarregadas = alvo.every(empresa => empresasCarregadasRef.current.has(empresa.id));

        if (todasCarregadas) {
          setLoading(false);
        }
      }
    };
    
    // Verificar se todas as empresas foram carregadas
    setTimeout(checkLoadingComplete, 200);
    
    // Timeout de segurança para evitar loading infinito
    const timeoutId = setTimeout(() => {
      if (thisRequest === requestIdRef.current && loadingRef.current) {
        console.warn('[EmpresasResumoCards] Timeout de segurança: finalizando loading forçadamente');
        setLoading(false);
      }
    }, 30000); // 30 segundos de timeout
    
    // Armazenar timeout ID para possível cancelamento
    if (thisRequest === requestIdRef.current) {
      // Limpar timeout anterior se existir
      if (window.empresasResumoTimeout) {
        clearTimeout(window.empresasResumoTimeout);
      }
      window.empresasResumoTimeout = timeoutId;
    }
  }, [executeRequest, getEmpresasAlvo, getPrefetchedData, hasPendingRequest]);

  // Monitorar quando todas as empresas foram carregadas
  useEffect(() => {
    if (loading && empresasCarregadas.size > 0) {
      const alvo = getEmpresasAlvo();
      const todasCarregadas = alvo.every(empresa => empresasCarregadas.has(empresa.id));
      
      if (todasCarregadas) {
        // Cancelar timeout de segurança quando loading finaliza corretamente
        if (window.empresasResumoTimeout) {
          clearTimeout(window.empresasResumoTimeout);
          window.empresasResumoTimeout = null;
        }
        setLoading(false);
      }
    }
  }, [empresasCarregadas, getEmpresasAlvo, loading]);

  useEffect(() => {
    // Verificar se já carregou
    if (hasLoadedRef.current) {
      return;
    }
    
    // Aguardar um pouco para garantir que o contexto está carregado
    const timer = setTimeout(() => {
      carregarDadosEmpresas();
    }, 100);
    
    const onChanged = () => {
      carregarDadosEmpresas(true); // Force refresh on company change
    };
    
    // Listener para quando a página se torna visível novamente (apenas se dados estão vazios ou com erro)
    const handleVisibilityChange = () => {
      if (!document.hidden && (dadosEmpresasRef.current.length === 0 || dadosEmpresasRef.current.every(empresa => empresa.status === 'ERRO'))) {
        // Página se tornou visível e não temos dados válidos, recarregar dados
        carregarDadosEmpresas(true);
      }
    };
    
    // Listener para detectar mudanças de rota (SPA) - apenas se necessário
    const handleRouteChange = () => {
      // Verificar se estamos no dashboard e se não temos dados carregados
      if ((window.location.pathname === '/dashboard' || window.location.pathname === '/') && 
          (dadosEmpresasRef.current.length === 0 || dadosEmpresasRef.current.every(empresa => empresa.status === 'ERRO'))) {
        carregarDadosEmpresas(true);
      }
    };
    
    window.addEventListener('company-changed', onChanged);
    window.addEventListener('company-context-changed', onChanged);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Listener para mudanças de rota (usando popstate para navegação do browser)
    window.addEventListener('popstate', handleRouteChange);
    
    // Listener customizado para mudanças de rota do React Router
    window.addEventListener('route-changed', handleRouteChange);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('company-changed', onChanged);
      window.removeEventListener('company-context-changed', onChanged);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('route-changed', handleRouteChange);
    };
  }, [carregarDadosEmpresas]); // Mantém o bootstrap estável

  // Verificação adicional de segurança para finalizar loading
  useEffect(() => {
    if (loading && dadosEmpresas.length > 0) {
      const alvo = getEmpresasAlvo();
      const todasTemDados = alvo.every(empresa => {
        const dadosEmpresa = dadosEmpresas.find(d => d.id === empresa.id);
        return dadosEmpresa && (dadosEmpresa.status === 'DADOS CARREGADOS' || dadosEmpresa.status === 'SEM DADOS' || dadosEmpresa.status === 'ERRO');
      });
      
      if (todasTemDados) {
        // Cancelar timeout de segurança quando loading finaliza corretamente
        if (window.empresasResumoTimeout) {
          clearTimeout(window.empresasResumoTimeout);
          window.empresasResumoTimeout = null;
        }
        setLoading(false);
      }
    }
  }, [dadosEmpresas, getEmpresasAlvo, loading]);

  // Detectar quando o componente se torna visível novamente
  useEffect(() => {
    const handleFocus = () => {
      if (isVisibleRef.current) {
        // Componente já estava visível, não fazer nada
        return;
      }
      
      isVisibleRef.current = true;
      
      // Verificar se os dados estão vazios ou com erro
      if (dadosEmpresasRef.current.length === 0 || dadosEmpresasRef.current.every(empresa => empresa.status === 'ERRO')) {
        // Recarregar dados se estiverem vazios ou com erro
        carregarDadosEmpresas();
      }
    };

    const handleBlur = () => {
      isVisibleRef.current = false;
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [carregarDadosEmpresas]);

  // Limpar timeout ao desmontar o componente
  useEffect(() => {
    return () => {
      if (window.empresasResumoTimeout) {
        clearTimeout(window.empresasResumoTimeout);
        window.empresasResumoTimeout = null;
      }
    };
  }, []);

  return {
    dadosEmpresas,
    loading,
    error,
    // Informações sobre progresso da renderização incremental
    progresso: {
      carregadas: empresasCarregadas.size,
      total: getEmpresasAlvo().length,
      percentual: getEmpresasAlvo().length > 0 ? 
        Math.round((empresasCarregadas.size / getEmpresasAlvo().length) * 100) : 0
    },
    recarregar: carregarDadosEmpresas,
    // Função para forçar refresh (ignora cache)
    refresh: () => carregarDadosEmpresas(true),
    // Função para limpar cache manualmente
    clearCache: () => {
      cache.clear();
    }
  };
};
